<template>
  <div class="relative">
    <div class="divide-y divide-gray-100 dark:divide-gray-700">
      <div
        v-for="(mail, index) in mails?.results"
        :key="mail.id"
        :data-index="index"
        :class="[
          selectedEmail?.id === mail.id
            ? 'bg-blue-50 dark:bg-blue-900/20 border-r-4 border-blue-600'
            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50',
          emailUnreadCalc(mail) ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-800/50'
        ]"
        class="group cursor-pointer transition-all duration-200 relative"
        @click="$emit('clicked', mail)">
        <!-- Unread indicator -->
        <div v-if="emailUnreadCalc(mail)" class="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-blue-600 rounded-r-full" />

        <div class="flex items-start p-4 space-x-3">
          <!-- Avatar -->
          <div class="flex-shrink-0 relative">
            <Avatar
              v-if="mail.meta_data?.from_?.length"
              :radius="40"
              :hover-effect="false"
              :first-name="
                mail.meta_data?.from_[0].name
                  ? mail.meta_data.from_[0].name.toLocaleUpperCase()
                  : mail.meta_data.from_[0].email.toLocaleUpperCase()
              "
              :last-name="
                mail.meta_data.from_[0].name?.split(' ')[1] ? mail.meta_data.from_[0].name.split(' ')[1].toLocaleUpperCase() : null
              "
              height="4" />

            <!-- Online status indicator -->
            <div class="absolute -bottom-0 -right-0 w-3 h-3 bg-green-400 border-2 border-white dark:border-gray-800 rounded-full" />
          </div>

          <!-- Email Content -->
          <div class="flex-1 min-w-0">
            <div class="flex items-start justify-between">
              <div class="flex-1 min-w-0">
                <!-- Sender and Thread Count -->
                <div class="flex items-center space-x-2 mb-1">
                  <h3
                    :class="[
                      emailUnreadCalc(mail) ? 'font-semibold text-gray-900 dark:text-white' : 'font-medium text-gray-700 dark:text-gray-300'
                    ]"
                    class="text-sm truncate">
                    {{ getThreadUsers(mail) }}
                  </h3>

                  <!-- Thread count badge -->
                  <span
                    v-if="mail.children?.length"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                    {{ mail.children.length + 1 }}
                  </span>

                  <!-- Starred indicator -->
                  <Star v-if="mail.meta_data.starred" class="w-4 h-4 text-yellow-400 fill-current flex-shrink-0" />
                </div>

                <!-- Subject -->
                <p
                  :class="[emailUnreadCalc(mail) ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400']"
                  class="text-sm truncate pr-2">
                  {{ mail.name || $t("(No Subject)") }}
                </p>

                <!-- Preview text -->
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                  {{ getEmailPreview(mail) }}
                </p>
              </div>

              <!-- Right side - Time and Actions -->
              <div class="flex flex-col items-end space-y-1 ml-2">
                <!-- Time -->
                <span class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {{ formatEmailTime(mail.interaction_date) }}
                </span>

                <!-- Action buttons - show on hover -->
                <div class="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <button
                    class="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-150"
                    :title="mail.meta_data.starred ? $t('Unstar') : $t('Star')"
                    @click.stop="$emit('clicked', mail, 'starred')">
                    <Star
                      :class="[mail.meta_data.starred ? 'text-yellow-400 fill-current' : 'text-gray-400 hover:text-yellow-400']"
                      class="w-4 h-4 transition-colors duration-150" />
                  </button>

                  <button
                    class="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-150"
                    :title="$t('Reply')"
                    @click.stop="$emit('clicked', mail, 'reply')">
                    <Reply class="w-4 h-4 text-gray-400 hover:text-blue-600 transition-colors duration-150" />
                  </button>

                  <button
                    class="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-150"
                    :title="$t('Forward')"
                    @click.stop="$emit('clicked', mail, 'forward')">
                    <Forward class="w-4 h-4 text-gray-400 hover:text-blue-600 transition-colors duration-150" />
                  </button>

                  <button
                    v-if="$route?.query?.tab !== 'trash'"
                    class="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-150"
                    :title="$t('Delete')"
                    @click.stop="$emit('clicked', mail, 'trash')">
                    <Trash2 class="w-4 h-4 text-gray-400 hover:text-red-600 transition-colors duration-150" />
                  </button>

                  <button
                    v-else
                    class="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-150"
                    :title="$t('Restore')"
                    @click.stop="$emit('clicked', mail, 'restore')">
                    <RefreshCcw class="w-4 h-4 text-gray-400 hover:text-green-600 transition-colors duration-150" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Avatar from "@/components/shared/Avatar.vue"
import { Star, Reply, Forward, Trash2, RefreshCcw } from "lucide-vue-next"

export default {
  components: {
    Avatar,
    Reply,
    Trash2,
    Forward,
    RefreshCcw,
    Star
  },
  props: {
    selectedEmail: {
      type: Object,
      required: false
    },
    mails: {
      type: Object,
      required: true
    }
  },
  emits: ["clicked", "deleteEmail", "listChanged"],

  activated() {
    this.$emit("listChanged", this.$route.query.tab)
  },

  created() {},
  methods: {
    emailUnreadCalc(mail) {
      if (mail.meta_data.unread) {
        return true
      }
      let any_child_unread = false
      mail.children?.forEach((child_email) => {
        if (child_email.meta_data.unread) {
          any_child_unread = true
        }
      })
      return any_child_unread
    },
    getThreadUsers(mail) {
      const thread_users_list = []

      if (!mail.children?.length) {
        thread_users_list.push(mail.meta_data.from_[0].name ? mail.meta_data.from_[0].name : mail.meta_data.from_[0].email)
      } else {
        if (mail.meta_data.from_[0].email == this.$store.state.auth.user.email) {
          thread_users_list.push("me")
        } else {
          thread_users_list.push(mail.meta_data.from_[0].name ? mail.meta_data.from_[0].name : mail.meta_data.from_[0].email)
        }

        mail.meta_data.to.forEach((user) => {
          let string_to_display = ""
          if (user.email == this.$store.state.auth.user.email) {
            string_to_display = "me"
          } else {
            string_to_display = user.name ? user.name.split(" ")[0] : user.email
          }
          if (!thread_users_list.includes(string_to_display)) {
            thread_users_list.push(string_to_display)
          }
        })
        mail.children?.forEach((child_mail) => {
          let string_to_display = ""
          if (child_mail.meta_data.from_[0].email == this.$store.state.auth.user.email) {
            string_to_display = "me"
          } else {
            string_to_display = child_mail.meta_data.from_[0].name
              ? child_mail.meta_data.from_[0].name.split(" ")[0]
              : child_mail.meta_data.from_[0].email
          }
          if (!thread_users_list.includes(string_to_display)) {
            thread_users_list.push(string_to_display)
          }

          child_mail.meta_data.to.forEach((user) => {
            let string_to_display = ""
            if (user.email == this.$store.state.auth.user.email) {
              string_to_display = "me"
            } else {
              string_to_display = user.name ? user.name.split(" ")[0] : user.email
            }
            if (!thread_users_list.includes(string_to_display)) {
              thread_users_list.push(string_to_display)
            }
          })
        })
      }

      return thread_users_list.join(", ")
    },
    getEmailPreview(mail) {
      // Extract plain text from email description for preview
      if (!mail.description) {
        return this.$t("No content")
      }

      const tempDiv = document.createElement("div")
      tempDiv.innerHTML = mail.description
      const plainText = tempDiv.textContent || tempDiv.innerText || ""
      return plainText.trim().substring(0, 100) + (plainText.length > 100 ? "..." : "")
    },
    formatEmailTime(dateString) {
      if (!dateString) {
        return ""
      }

      const emailDate = new Date(dateString)
      const now = new Date()
      const diffMs = now - emailDate
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

      if (diffHours < 1) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60))
        return diffMinutes < 1 ? this.$t("Just now") : `${diffMinutes}m`
      } else if (diffHours < 24) {
        return `${diffHours}h`
      } else if (diffDays < 7) {
        return `${diffDays}d`
      } else {
        return emailDate.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: emailDate.getFullYear() !== now.getFullYear() ? "numeric" : undefined
        })
      }
    }
  }
}
</script>
